// ═══════════════════════════════════════════════════════════════
// GuideReaderModal — Full-screen overlay to read a structured guide
// Path: src/components/ui/GuideReaderModal.jsx
//
// Consumes a guide object from src/constants/guides.js and renders
// it with readable typography. Centered card, scrollable body,
// ESC / outside click to close.
// ═══════════════════════════════════════════════════════════════

import React, { useEffect } from "react";
import { X, BookOpen, Printer } from "lucide-react";
import { useApp } from "../../context/AppContext";
import { FONT_SIZES, FONT_FAMILY, ICON_SIZES } from "../../constants/appConfig";

const Block = ({ block, palette }) => {
  if (block.type === "heading") {
    const sizes = {
      1: { fontSize: FONT_SIZES.xxxl, marginTop: 0, marginBottom: 8 },
      2: { fontSize: FONT_SIZES.xxl, marginTop: 32, marginBottom: 12 },
      3: { fontSize: FONT_SIZES.lg, marginTop: 20, marginBottom: 8 },
    };
    const Tag = `h${block.level || 2}`;
    return (
      <Tag
        style={{
          ...sizes[block.level || 2],
          fontWeight: 700,
          color: palette.text1,
          fontFamily: FONT_FAMILY,
          lineHeight: 1.25,
        }}
      >
        {block.text}
      </Tag>
    );
  }

  if (block.type === "paragraph") {
    return (
      <p
        style={{
          fontSize: FONT_SIZES.base,
          color: palette.text1,
          lineHeight: 1.7,
          margin: "0 0 14px 0",
          fontFamily: FONT_FAMILY,
        }}
      >
        {block.text}
      </p>
    );
  }

  if (block.type === "list") {
    return (
      <ul
        style={{
          margin: "0 0 16px 0",
          paddingLeft: 22,
          color: palette.text1,
          fontSize: FONT_SIZES.base,
          lineHeight: 1.7,
          fontFamily: FONT_FAMILY,
        }}
      >
        {block.items.map((item, i) => (
          <li key={i} style={{ marginBottom: 6 }}>
            {item}
          </li>
        ))}
      </ul>
    );
  }

  if (block.type === "table") {
    return (
      <div
        style={{
          margin: "4px 0 20px 0",
          border: `1px solid ${palette.cardBorder}`,
          borderRadius: 8,
          overflow: "hidden",
        }}
      >
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontFamily: FONT_FAMILY,
            fontSize: FONT_SIZES.sm,
          }}
        >
          <thead>
            <tr style={{ background: `${palette.accent1}10` }}>
              {block.headers.map((h, i) => (
                <th
                  key={i}
                  style={{
                    textAlign: "left",
                    padding: "10px 12px",
                    color: palette.text1,
                    fontWeight: 700,
                    borderBottom: `1px solid ${palette.cardBorder}`,
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {block.rows.map((row, ri) => (
              <tr
                key={ri}
                style={{
                  borderBottom:
                    ri < block.rows.length - 1
                      ? `1px solid ${palette.cardBorder}`
                      : "none",
                }}
              >
                {row.map((cell, ci) => (
                  <td
                    key={ci}
                    style={{
                      padding: "10px 12px",
                      color: palette.text2,
                      verticalAlign: "top",
                    }}
                  >
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (block.type === "callout") {
    const bg = block.variant === "warn" ? "#fef3c7" : `${palette.accent1}10`;
    const border =
      block.variant === "warn" ? "#fde68a" : palette.cardBorder;
    const color = block.variant === "warn" ? "#78350f" : palette.text1;
    return (
      <div
        style={{
          padding: "14px 16px",
          borderRadius: 8,
          background: bg,
          borderLeft: `4px solid ${border}`,
          fontSize: FONT_SIZES.base,
          color,
          fontFamily: FONT_FAMILY,
          lineHeight: 1.6,
          margin: "4px 0 20px 0",
          fontStyle: "italic",
        }}
      >
        {block.text}
      </div>
    );
  }

  if (block.type === "divider") {
    return (
      <hr
        style={{
          border: "none",
          borderTop: `1px solid ${palette.cardBorder}`,
          margin: "28px 0",
        }}
      />
    );
  }

  return null;
};

export const GuideReaderModal = ({ guide, onClose }) => {
  const { palette } = useApp();

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [onClose]);

  if (!guide) return null;

  const handlePrint = () => {
    window.print();
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.55)",
        zIndex: 1000,
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        padding: "40px 20px",
        overflowY: "auto",
        fontFamily: FONT_FAMILY,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: palette.cardBg,
          borderRadius: 12,
          width: "100%",
          maxWidth: 780,
          maxHeight: "calc(100vh - 80px)",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 20px 60px rgba(0,0,0,0.35)",
          overflow: "hidden",
        }}
      >
        {/* Header bar */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "14px 20px",
            borderBottom: `1px solid ${palette.cardBorder}`,
            background: `${palette.accent1}08`,
          }}
        >
          <BookOpen size={ICON_SIZES.md} color={palette.accent1} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontSize: FONT_SIZES.sm,
                fontWeight: 700,
                color: palette.text1,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {guide.title}
            </div>
            {guide.readingTime && (
              <div
                style={{
                  fontSize: 11,
                  color: palette.text3,
                }}
              >
                {guide.readingTime}
              </div>
            )}
          </div>
          <button
            onClick={handlePrint}
            title="Print"
            style={{
              background: "transparent",
              border: `1px solid ${palette.cardBorder}`,
              borderRadius: 6,
              padding: "6px 10px",
              cursor: "pointer",
              color: palette.text2,
              display: "flex",
              alignItems: "center",
              gap: 4,
              fontSize: FONT_SIZES.xs,
              fontFamily: FONT_FAMILY,
              fontWeight: 600,
            }}
          >
            <Printer size={ICON_SIZES.sm} />
            Print
          </button>
          <button
            onClick={onClose}
            title="Close"
            style={{
              background: "transparent",
              border: "none",
              padding: 6,
              cursor: "pointer",
              color: palette.text2,
              display: "flex",
              alignItems: "center",
            }}
          >
            <X size={ICON_SIZES.md} />
          </button>
        </div>

        {/* Scrollable body */}
        <div
          className="guide-reader-body"
          style={{
            flex: 1,
            overflowY: "auto",
            padding: "32px 40px",
          }}
        >
          <h1
            style={{
              margin: 0,
              fontSize: FONT_SIZES.xxxl,
              fontWeight: 800,
              color: palette.text1,
              lineHeight: 1.2,
            }}
          >
            {guide.title}
          </h1>
          {guide.subtitle && (
            <p
              style={{
                marginTop: 6,
                marginBottom: 4,
                fontSize: FONT_SIZES.lg,
                color: palette.text2,
                fontWeight: 400,
                lineHeight: 1.4,
              }}
            >
              {guide.subtitle}
            </p>
          )}
          {guide.meta && (
            <div
              style={{
                fontSize: FONT_SIZES.xs,
                color: palette.text3,
                fontStyle: "italic",
                marginBottom: 24,
              }}
            >
              {guide.meta}
            </div>
          )}

          {guide.blocks.map((block, i) => (
            <Block key={i} block={block} palette={palette} />
          ))}
        </div>
      </div>
    </div>
  );
};
